import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [eid, setEid] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/pegawai"} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(eid.trim(), pw);
      toast.success("Login berhasil");
      nav(u.role === "admin" ? "/admin" : "/pegawai");
    } catch (err) {
      // Build informative error message
      let msg = "Login gagal";
      if (err?.response) {
        // Server merespon dengan status error
        const status = err.response.status;
        const detail = err.response.data?.detail || err.response.statusText || "Unknown";
        msg = `[HTTP ${status}] ${detail}`;
      } else if (err?.request) {
        // Request terkirim tapi tidak ada response (network / CORS / server down)
        msg = `Tidak ada respon dari server. Periksa koneksi internet atau hubungi admin. (${err.message || "Network Error"})`;
      } else {
        // Error lain (config, dll)
        msg = `Error: ${err?.message || "Unknown"}`;
      }
      toast.error(msg, { duration: 8000 });
      console.error("Login error:", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img
          src="https://images.pexels.com/photos/12843342/pexels-photo-12843342.jpeg"
          alt="bg"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary flex items-center justify-center font-display font-bold rounded-sm">
              RL
            </div>
            <span className="font-display text-xl font-semibold">RelayLab Autolight</span>
          </div>
          <div>
            <p className="overline text-zinc-400 mb-2">Sistem Internal</p>
            <h1 className="font-display text-4xl xl:text-5xl font-bold tracking-tight leading-tight">
              Presensi, Lembur, Kasbon — semua dalam satu app.
            </h1>
            <p className="mt-4 text-zinc-300 max-w-md">
              Akses cepat dari HP, transparan untuk pegawai, terkontrol untuk admin.
            </p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 bg-white">
        <form
          onSubmit={submit}
          className="w-full max-w-sm space-y-6"
          data-testid="login-form"
        >
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 bg-primary text-white font-display font-bold flex items-center justify-center rounded-sm">
              RL
            </div>
            <span className="font-display font-semibold">RelayLab Presensi</span>
          </div>
          <div>
            <p className="overline mb-2">Masuk</p>
            <h2 className="font-display text-3xl font-bold tracking-tight">
              Selamat datang
            </h2>
            <p className="text-sm text-zinc-500 mt-1">
              Masuk dengan Employee ID Anda.
            </p>
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="eid">Employee ID</Label>
              <Input
                id="eid"
                value={eid}
                onChange={(e) => setEid(e.target.value)}
                placeholder="contoh: admin"
                required
                data-testid="input-employee-id"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="pw">Password</Label>
              <Input
                id="pw"
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
                data-testid="input-password"
                className="mt-1.5"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={busy}
            data-testid="btn-login"
            className="w-full h-11 bg-primary hover:bg-primary/90"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Masuk"}
          </Button>
          <div className="text-center mt-4">
            <a
              href="/status"
              className="text-xs text-zinc-500 hover:text-primary inline-flex items-center gap-1.5"
              data-testid="link-status"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              Cek Status Sistem
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
